import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, message, Space, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";

import DataTableBase from "../../../shared/dashboards/DataTable/DataTable";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../../utils/dateService";
import {
  FRAUD_DETECTION_RULE_ID_QUERY_KEY,
  NAVIGATION_ROUTES,
} from "../../../../utils/NavigationUtils";

import CustomFormInput from "../../../CommonComponents/CustomFormInput/CustomFormInput";
import CustomSelect from "../../../CommonComponents/CustomSelect/CustomSelect";

import {
  useGetFraudRules,
  type FraudGlRuleLevel,
  type FraudRuleCondition,
} from "../../../../queries/fraud-detection/useGetFraudRules";
import { useToggleFraudRuleStatus } from "../../../../queries/fraud-detection/useToggleFraudRuleStatus";
import { formatCurrency } from "../../../../utils/financialFormatters";

import ToggleSwitch from "../../../CommonComponents/ToggleSwitch/ToggleSwitch";

import styles from "./stylesFraudDetectionRulesTable.module.css";

export type { FraudRuleCondition };

type FraudRuleSearchBy = "name" | "status" | "condition";

export type FraudDetectionRuleRow = {
  id: string;
  name: string;
  status: string;
  trigger_action: string[];
  conditions: FraudRuleCondition[];
  violators_count: number;
  last_modified: string | null;
  editor: string;
  /** Present when row comes from GET /fraud/rule (GLI rule). */
  deposit_threshold?: string | number;
  evaluation_window_seconds?: number;
  active?: boolean;
  levels?: FraudGlRuleLevel[];
};

function ruleRowIsActive(row: FraudDetectionRuleRow): boolean {
  if (row.active !== undefined) return row.active;
  return row.status.trim().toLowerCase() === "active";
}

function formatConditionLine(
  condition: FraudRuleCondition,
  currencySymbol?: string,
): string {
  const n = Number(String(condition.threshold_value).replace(/[^0-9.-]/g, ""));
  const amount = Number.isFinite(n)
    ? formatCurrency(n, currencySymbol, 0)
    : condition.threshold_value;
  return `Threshold Value: ${amount} Period: ${condition.period}`;
}

function filterFraudDetectionRules(
  rows: FraudDetectionRuleRow[],
  searchBy: FraudRuleSearchBy | undefined,
  searchText: string | undefined,
  currencySymbol?: string,
): FraudDetectionRuleRow[] {
  const q = (searchText ?? "").trim().toLowerCase();
  if (!q) return rows;
  const by: FraudRuleSearchBy = searchBy ?? "name";
  return rows.filter((row) => {
    if (by === "name") {
      return row.name.toLowerCase().includes(q);
    }
    if (by === "status") {
      return row.status.toLowerCase().includes(q);
    }
    return row.conditions.some((c) =>
      formatConditionLine(c, currencySymbol).toLowerCase().includes(q),
    );
  });
}

const DataTable = DataTableBase as React.ComponentType<{
  id?: string;
  rowKey?: string | ((record: FraudDetectionRuleRow) => string);
  columns?: ColumnsType<FraudDetectionRuleRow>;
  dataSource?: FraudDetectionRuleRow[];
  pagination?: boolean;
  scrollPreset?: string;
  loading?: boolean;
  className?: string;
}>;

export const MOCK_FRAUD_RULES: FraudDetectionRuleRow[] = [
  {
    id: "rule-mock-1",
    name: "AML Detection",
    status: "Active",
    trigger_action: ["Deposit +", "Wager +", "Withdrawal"],
    conditions: [
      { threshold_value: "10000", period: "Week" },
      { threshold_value: "10000", period: "Week" },
      { threshold_value: "10000", period: "Week" },
    ],
    violators_count: 12,
    last_modified: "Oct 05, 2024 12:34 PM",
    editor: "agent@example.com",
    deposit_threshold: "10000",
    evaluation_window_seconds: 604800,
    active: true,
  },
];

export type FraudDetectionRulesTableProps = {
  dataSource?: FraudDetectionRuleRow[];
  loading?: boolean;
  onEditRule?: (record: FraudDetectionRuleRow) => void;
  currencySymbol?: string;
};

const FraudDetectionRulesTable: React.FC<FraudDetectionRulesTableProps> = ({
  dataSource: dataSourceProp,
  loading: loadingProp,
  onEditRule,
  currencySymbol,
}) => {
  const navigate = useNavigate();

  const { data: hookData, isLoading: hookLoading } = useGetFraudRules();
  const { mutate: setRuleActive, isPending: isTogglingRuleActive } =
    useToggleFraudRuleStatus();

  const dataSource = useMemo(
    () => dataSourceProp ?? hookData ?? [],
    [dataSourceProp, hookData],
  );
  const loading = loadingProp ?? hookLoading;

  const [form] = Form.useForm<{
    searchBy: FraudRuleSearchBy;
    searchText: string;
  }>();

  const searchBy = Form.useWatch("searchBy", form);
  const searchText = Form.useWatch("searchText", form);

  const filteredDataSource = useMemo(
    () =>
      filterFraudDetectionRules(
        dataSource,
        searchBy,
        searchText,
        currencySymbol,
      ),
    [currencySymbol, dataSource, searchBy, searchText],
  );

  const SEARCH_BY_OPTIONS: { value: FraudRuleSearchBy; label: string }[] = [
    { value: "name", label: AppStrings.NAME },
    { value: "status", label: AppStrings.STATUS },
    { value: "condition", label: AppStrings.CONDITION },
  ];

  const handleEditRule = useCallback(
    (record: FraudDetectionRuleRow) => {
      if (onEditRule) {
        onEditRule(record);
        return;
      }
      const params = new URLSearchParams({
        [FRAUD_DETECTION_RULE_ID_QUERY_KEY]: record.id,
      });
      navigate(`${NAVIGATION_ROUTES.FRAUD_DETECTION_EDIT_RULE}?${params.toString()}`, {
        state: { rule: record },
      });
    },
    [navigate, onEditRule],
  );

  const columns: ColumnsType<FraudDetectionRuleRow> = useMemo(
    () => [
      {
        title: AppStrings.NAME,
        dataIndex: "name",
        key: "name",
        width: 180,
      },
      {
        title: AppStrings.STATUS,
        dataIndex: "status",
        key: "status",
        width: 120,
        align: "center",
        render: (_: string, record: FraudDetectionRuleRow) => (
          <ToggleSwitch
            status={ruleRowIsActive(record)}
            eventCode={record.id}
            updateEventCode={(payload: { is_active: boolean }) => {
              setRuleActive(
                { active: payload.is_active },
                {
                  onSuccess: () => {
                    message.success(AppStrings.VALUES_UPDATED_SUCCESSFULLY);
                  },
                  onError: () => {
                    message.error(AppStrings.FAILED_TO_UPDATE_STATUS);
                  },
                },
              );
            }}
            data={null}
          />
        ),
      },
      {
        title: AppStrings.TRIGGER_ACTION,
        dataIndex: "trigger_action",
        key: "trigger_action",
        width: 200,
        ellipsis: false, // chips, don't truncate
        render: (items: string[]) =>
          items?.length ? (
            <div className={styles.fraudDetectionTriggerActionsCell}>
              {items.map((label, index) => (
                <span key={`${label}-${index}`} className={styles.fraudDetectionTriggerChip}>
                  {label}
                </span>
              ))}
            </div>
          ) : (
            "—"
          ),
      },
      {
        title: AppStrings.CONDITIONS_TITLE,
        dataIndex: "conditions",
        key: "conditions",
        width: 260,
        ellipsis: false, // multi-line list; per-line title attrs handle hover
        render: (items: FraudRuleCondition[]) =>
          items?.length ? (
            <div className={styles.fraudDetectionConditionsCell}>
              {items.map((condition, index) => {
                const line = formatConditionLine(condition, currencySymbol);
                return (
                  <div
                    key={`${line}-${index}`}
                    className={styles.fraudDetectionConditionLine}
                    title={line}
                  >
                    {line}
                  </div>
                );
              })}
            </div>
          ) : (
            "—"
          ),
      },
      {
        title: AppStrings.VIOLATORS,
        dataIndex: "violators_count",
        key: "violators_count",
        width: 130,
      },
      {
        title: AppStrings.LAST_MODIFIED,
        dataIndex: "last_modified",
        key: "last_modified",
        width: 180,
        render: (value: string | null) =>
          value ? convertToUserTimezone(value) : "—",
      },
      {
        title: AppStrings.EDITOR,
        dataIndex: "editor",
        key: "editor",
        width: 160,
      },
      {
        title: AppStrings.EDIT,
        key: "edit",
        width: 88,
        fixed: "right",
        align: "center",
        render: (_: unknown, record) => (
          <Space size="small">
            <Button
              className={styles.fraudDetectionEditButton}
              type="text"
              icon={<EditOutlined />}
              aria-label={AppStrings.EDIT}
              onClick={() => handleEditRule(record)}
            />
          </Space>
        ),
      },
    ],
    [currencySymbol, handleEditRule, setRuleActive],
  );

  return (
    <div
      className={styles.fraudDetectionRulesTable}
      aria-busy={isTogglingRuleActive}
    >
      <h3 className={styles.fraudDetectionRulesTableHeaderTitle}>
        {AppStrings.RULES}
      </h3>
      <Form
        form={form}
        initialValues={{
          searchBy: "name",
          searchText: "",
        }}
      >
        <div className={styles.fraudDetectionRulesFilters}>
          <div className={styles.fraudDetectionFilterField}>
            <Form.Item name="searchBy" noStyle>
              <CustomSelect<FraudRuleSearchBy>
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={SEARCH_BY_OPTIONS}
              />
            </Form.Item>
          </div>
          <div className={styles.fraudDetectionFilterField}>
            <Form.Item name="searchText" noStyle>
              <CustomFormInput
                allowClear
                className={styles.fraudDetectionFilterInput}
                placeholder={AppStrings.SEARCH}
                prefix={<SearchOutlined />}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <DataTable
        id="fraud-detection-rules"
        rowKey="id"
        columns={columns}
        dataSource={filteredDataSource}
        loading={loading}
        pagination={false}
        scrollPreset="wide"
      />
      {isTogglingRuleActive ? (
        <div
          className={styles.savingOverlay}
          role="status"
          aria-live="polite"
          aria-label={AppStrings.SAVING}
          data-testid="fraud-rules-toggle-saving-overlay"
        >
          <div className={styles.savingSpinStack}>
            <Spin size="large" className={styles.savingSpin} />
            <span className={styles.savingLabel}>{AppStrings.SAVING}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FraudDetectionRulesTable;
